/**
 * WhatsApp Notification via Webhook
 *
 * This utility sends form data to a webhook endpoint that then
 * forwards it as a WhatsApp message via WhatsApp Business API.
 *
 * SETUP OPTIONS (choose one):
 *
 * 1. Make.com (Recommended - Free tier available):
 *    - Create a scenario with "Webhook" trigger → "WhatsApp Business Cloud API" action
 *    - Paste the webhook URL below
 *
 * 2. n8n (Self-hosted, free):
 *    - Create a workflow with Webhook node → WhatsApp node
 *
 * 3. Zapier:
 *    - Create a Zap with "Webhooks by Zapier" trigger → WhatsApp action
 *
 * 4. Custom serverless function (Cloudflare Workers, Vercel Edge, etc.)
 *    - Deploy a small function that receives the data and calls WhatsApp Cloud API
 */

// ⚠️ REPLACE THIS with your actual webhook URL from Make.com, Zapier, n8n, etc.
const WEBHOOK_URL = 'https://hook.us1.make.com/YOUR_WEBHOOK_ID_HERE'

// WhatsApp number to receive notifications (Pedro's number)
const NOTIFICATION_PHONE = '18572715381'

export interface LeadData {
  name: string
  phone: string
  email: string
  vehicle: string
  creditScore: string
  language: string
  source: string
  timestamp: string
}

/**
 * Sends lead data to a webhook that forwards it to WhatsApp
 */
export const sendWhatsAppNotification = async (
  data: LeadData,
): Promise<boolean> => {
  // Don't send if webhook URL hasn't been configured
  if (WEBHOOK_URL.includes('YOUR_WEBHOOK_ID_HERE')) {
    console.warn(
      '⚠️ WhatsApp webhook URL not configured. Edit utils/whatsappNotification.ts and replace WEBHOOK_URL with your actual webhook URL.',
    )
    return false
  }

  try {
    // Format the message that will be sent via WhatsApp
    const message = formatLeadMessage(data)

    const payload = {
      phone: NOTIFICATION_PHONE,
      message,
      // Raw data for the webhook to use however needed
      leadData: data,
    }

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (response.ok) {
      console.log('✅ WhatsApp notification sent successfully')
      return true
    } else {
      console.error('❌ WhatsApp notification failed:', response.status)
      return false
    }
  } catch (error) {
    console.error('❌ Error sending WhatsApp notification:', error)
    // Don't block the form submission if notification fails
    return false
  }
}

/**
 * Formats the lead data into a readable WhatsApp message
 */
const formatLeadMessage = (data: LeadData): string => {
  const creditLabels: Record<string, string> = {
    no_credit: '🆕 Sin crédito / Nuevo',
    poor: '🔴 Malo (<580)',
    fair: '🟡 Regular (580-669)',
    good: '🟢 Bueno (670-739)',
    excellent: '⭐ Excelente (740+)',
  }

  const creditDisplay = creditLabels[data.creditScore] || data.creditScore

  return `🚗 *NUEVO LEAD - Landing Page*

👤 *Nombre:* ${data.name}
📱 *Teléfono:* ${data.phone}
📧 *Email:* ${data.email}
🚙 *Vehículo:* ${data.vehicle}
💳 *Crédito:* ${creditDisplay}
🌐 *Idioma:* ${data.language === 'es' ? 'Español' : 'English'}
📍 *Fuente:* ${data.source}
🕐 *Fecha:* ${data.timestamp}`
}
